var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import UPDATE_GLOBAL_EXCHANGE_RATE from 'api/mutations/UPDATE_GLOBAL_EXCHANGE_RATE';
import AUTOCOMPLETE_CURRENCIES from 'api/queries/AUTOCOMPLETE_CURRENCIES';
import GET_GLOBAL_EXCHANGE_RATES from 'api/queries/GET_GLOBAL_EXCHANGE_RATES';
const initialState = {
    currencies: [],
    toCurrency: {
        toCurrencyId: 'all',
        currencyIsoCode: 'all',
    },
    globalExchangeRates: [],
    status: {
        fetch: 'idle',
        fetchGlobalExchangeRates: 'idle',
        update: 'idle',
    },
    errors: {
        fetch: null,
        fetchGlobalExchangeRates: null,
        update: null,
    },
    refetchFlags: {
        fetch: false,
        fetchGlobalExchangeRates: false,
        update: false,
    },
};
export const fetchCurrencies = createAsyncThunk('currencies/fetchCurrencies', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: AUTOCOMPLETE_CURRENCIES,
            fetchPolicy: 'network-only',
        });
        return response.data.autocomplete.currencies.edges.map((item) => item.node);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchGlobalExchangeRates = createAsyncThunk('currencies/fetchGlobalExchangeRates', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ toCurrencyId, currencyIsoCode }, { rejectWithValue }) {
    try {
        const response = yield client.query({
            query: GET_GLOBAL_EXCHANGE_RATES,
            variables: toCurrencyId === 'all'
                ? undefined
                : {
                    toCurrencyId,
                    currencyIsoCode,
                },
            fetchPolicy: 'network-only',
        });
        return response.data.autocomplete.globalExchangeRates;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateGlobalExchangeRate = createAsyncThunk('currencies/updateGlobalExchangeRate', (_a) => __awaiter(void 0, [_a], void 0, function* ({ globalExchangeRateId, newRate }) {
    try {
        const currencyData = yield client.mutate({
            mutation: UPDATE_GLOBAL_EXCHANGE_RATE,
            variables: { globalExchangeRateId, newRate },
        });
        return currencyData.data.updateCurrency.currency;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
const currenciesSlice = createSlice({
    name: 'currencies',
    initialState,
    reducers: {
        resetCurrencies(state) {
            state.currencies = [];
            state.globalExchangeRates = [];
            state.toCurrency = {
                toCurrencyId: 'all',
                currencyIsoCode: 'all',
            };
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
        setToCurrency(state, action) {
            const { toCurrencyId, currencyIsoCode } = action.payload;
            state.toCurrency.toCurrencyId = toCurrencyId;
            state.toCurrency.currencyIsoCode = currencyIsoCode;
            state.refetchFlags.fetchGlobalExchangeRates = true;
        },
        updateGlobalExchangeRatesStore: (state, action) => {
            const { globalExchangeRateId, newRate } = action.payload;
            const itemIndex = state.globalExchangeRates.findIndex((item) => item.id === globalExchangeRateId);
            if (itemIndex !== -1) {
                state.globalExchangeRates[itemIndex] = Object.assign(Object.assign({}, state.globalExchangeRates[itemIndex]), { rate: parseFloat(String(newRate)
                        .replace(',', '.')
                        .replace(/[^0-9.,]/g, '')) });
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrencies.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchCurrencies.fulfilled, (state, action) => {
            state.status.fetch = 'succeeded';
            state.currencies = action.payload;
        })
            .addCase(fetchCurrencies.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(fetchGlobalExchangeRates.pending, (state) => {
            state.status.fetchGlobalExchangeRates = 'loading';
            state.refetchFlags.fetchGlobalExchangeRates = false;
        })
            .addCase(fetchGlobalExchangeRates.fulfilled, (state, action) => {
            state.status.fetchGlobalExchangeRates = 'succeeded';
            state.globalExchangeRates = action.payload;
        })
            .addCase(fetchGlobalExchangeRates.rejected, (state, action) => {
            state.status.fetchGlobalExchangeRates = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(updateGlobalExchangeRate.pending, (state) => {
            state.status.update = 'loading';
            state.refetchFlags.update = false;
        })
            .addCase(updateGlobalExchangeRate.fulfilled, (state) => {
            state.status.update = 'succeeded';
        })
            .addCase(updateGlobalExchangeRate.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.error.message || 'Error fetching components';
        });
    },
});
export const { resetCurrencies, setToCurrency, updateGlobalExchangeRatesStore } = currenciesSlice.actions;
export const selectCurrencies = (state) => state.parameters.currencies.currencies;
export const selectGlobalExchangeRates = (state) => state.parameters.currencies.globalExchangeRates;
export const selectToCurrency = (state) => state.parameters.currencies.toCurrency;
export const selectCurrencyById = (state, currencyId) => state.parameters.currencies.items.find((currency) => currency.id === currencyId);
export const selectCurrenciesPageInfo = (state) => state.parameters.currencies.pageInfo;
export const selectCurrenciesStatus = (state) => state.parameters.currencies.status;
export const selectCurrenciesErrors = (state) => state.parameters.currencies.errors;
export const selectCurrenciesRefetchFlags = (state) => state.parameters.currencies.refetchFlags;
export default currenciesSlice.reducer;
